import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useLocation, useNavigate } from 'js/lib/useRouter';

import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';

import {
  ALTERNATIVE_EMAIL_CACHE_DAYS,
  ALTERNATIVE_EMAIL_COURSE_PROGRESS_CACHE_DAYS,
  ALTERNATIVE_EMAIL_NEVER_EXPIRY,
} from 'bundles/enterprise-legacy-learner-home/constants/forcedSSORecoveryEmail';
import type { RecoveryEmail } from 'bundles/enterprise-legacy-learner-home/types/recoveryEmail';
import storageUtils from 'bundles/enterprise-legacy-learner-home/utils/storageUtils';
import { GdprSettingsQuery } from 'bundles/saml-sso/components/queries/GdprSettingsQuery';
import type {
  GdprSettingsQuery as GdprSettingsQueryType,
  GdprSettingsQueryVariables,
} from 'bundles/saml-sso/components/queries/__generated__/GdprSettingsQuery';
import { useEnterpriseFeatureFlags } from 'bundles/saml-sso/shared/hooks/useProductFeatureFlags';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import useOrgScopedSignificantCourseProgress from 'bundles/unified-home-common/hooks/useOrgScopedSignificantCourseProgress';

// Storage key for org-scoped recovery email modal (uses thirdPartyOrgId instead of programId)
const ORG_SCOPED_STORAGE_KEY = 'org-home-hideForceSSOAlternativeEmailModal';

/**
 * Org-scoped version of useAlternativeEmailModal.
 * Determines whether to show the ForcedSSORecoveryEmailModal at the org level.
 * Uses thirdPartyOrgId instead of programId for storage keys.
 */
const useOrgScopedAlternativeEmailModal = (
  userId: number,
  userEmails: UserEmail[] | undefined,
  recoveryEmail: RecoveryEmail | undefined | null
) => {
  const thirdPartyOrg = useThirdPartyOrganization();
  const thirdPartyOrgId = thirdPartyOrg?.id || '';

  const { loading: gdprSettingsLoading, data: gdprSettingsData } = useQuery<
    GdprSettingsQueryType,
    GdprSettingsQueryVariables
  >(GdprSettingsQuery, {
    variables: { thirdPartyOrganizationId: thirdPartyOrgId },
    skip: !thirdPartyOrgId,
    ssr: false,
  });
  const gdprSettings = gdprSettingsData?.GdprSettingsV1Resource?.get;

  const { isUnifiedSsoEnabled: isUnifiedSso, isLoading: isUnifiedSsoLoading } =
    useEnterpriseFeatureFlags(thirdPartyOrgId);

  const location = useLocation();
  const navigate = useNavigate();
  const [isModalOpen, setModalOpen] = React.useState(false);

  // Check localStorage before making expensive course progress queries.
  // If the modal is already suppressed (user dismissed it or has verified email),
  // skip the queries entirely.
  const isModalSuppressedByStorage = React.useMemo(() => {
    if (!userId || !thirdPartyOrgId) return true;
    const storedExpiry = storageUtils.get<number>(userId, thirdPartyOrgId, ORG_SCOPED_STORAGE_KEY, 0);
    return storedExpiry >= Date.now();
  }, [userId, thirdPartyOrgId]);

  const { hasSignificantCourseProgress, isLoading: isCourseProgressLoading } = useOrgScopedSignificantCourseProgress({
    skip: isModalSuppressedByStorage,
  });

  const setModalQueryParam = React.useCallback<(isOpen: boolean) => void>(
    (isOpen: boolean) => {
      navigate({
        pathname: location.pathname,
        search: '?' + new URLSearchParams({ ...location.query, ...{ openRecoveryModal: `${isOpen}` } }).toString(),
      });
    },
    [location.pathname, location.query, navigate]
  );

  const isLoading = gdprSettingsLoading || isUnifiedSsoLoading || isCourseProgressLoading;

  React.useEffect(() => {
    const hasVerifiedSecondaryEmail = userEmails?.some((email) => !email.isPrimary && email.isVerified);

    // If URL explicitly has openRecoveryModal=true, respect it and don't auto-close
    // This allows manual testing via URL param
    const isExplicitlyOpenedViaUrl = location.query.openRecoveryModal === 'true';

    // Wait till API requests have completed before checking if the modal should be shown
    if (isLoading || !userEmails) {
      return;
    }

    // Don't show modal if Unified SSO is enabled but recovery account on home page is disabled
    // (unless explicitly opened via URL for testing)
    if (isUnifiedSso && !gdprSettings?.recoveryAccountOnHomePage && !isExplicitlyOpenedViaUrl) {
      setModalOpen(false);
      storageUtils.set(userId, thirdPartyOrgId, ORG_SCOPED_STORAGE_KEY, Date.now() + ALTERNATIVE_EMAIL_NEVER_EXPIRY);
      return;
    }

    // Don't show modal if user already has a verified recovery email
    // (unless explicitly opened via URL for testing)
    if (recoveryEmail && recoveryEmail.isVerified && !isExplicitlyOpenedViaUrl) {
      setModalOpen(false);
      storageUtils.set(userId, thirdPartyOrgId, ORG_SCOPED_STORAGE_KEY, Date.now() + ALTERNATIVE_EMAIL_NEVER_EXPIRY);
      return;
    }

    // Don't show modal if user has a verified secondary email
    // (unless explicitly opened via URL for testing)
    if (hasVerifiedSecondaryEmail && !isExplicitlyOpenedViaUrl) {
      setModalOpen(false);
      storageUtils.set(userId, thirdPartyOrgId, ORG_SCOPED_STORAGE_KEY, Date.now() + ALTERNATIVE_EMAIL_NEVER_EXPIRY);
      return;
    }

    // Check if the storage cache has expired
    const shouldOpenAfterStoragePersistedDuration =
      storageUtils.get(userId, thirdPartyOrgId, ORG_SCOPED_STORAGE_KEY, Infinity) < Date.now();

    // Show modal if storage cache has expired and user has significant course progress
    if (shouldOpenAfterStoragePersistedDuration && hasSignificantCourseProgress) {
      setModalQueryParam(true);
    }
  }, [
    userId,
    userEmails,
    thirdPartyOrgId,
    hasSignificantCourseProgress,
    gdprSettings,
    isUnifiedSso,
    isLoading,
    recoveryEmail,
    setModalQueryParam,
    location.query.openRecoveryModal,
  ]);

  React.useEffect(() => {
    setModalOpen(location.query.openRecoveryModal === 'true');
  }, [location.query.openRecoveryModal]);

  const onClose = React.useCallback<() => void>(() => {
    setModalQueryParam(false);
  }, [setModalQueryParam]);

  const onDefer = React.useCallback<() => void>(() => {
    const dismissedForDuration = hasSignificantCourseProgress
      ? ALTERNATIVE_EMAIL_COURSE_PROGRESS_CACHE_DAYS
      : ALTERNATIVE_EMAIL_CACHE_DAYS;
    storageUtils.set(userId, thirdPartyOrgId, ORG_SCOPED_STORAGE_KEY, Date.now() + dismissedForDuration);
    onClose();
  }, [hasSignificantCourseProgress, userId, thirdPartyOrgId, onClose]);

  return {
    isOpen: isModalOpen,
    onClose: hasSignificantCourseProgress ? onDefer : onClose,
    shouldDeferOnClose: hasSignificantCourseProgress,
    isLoading,
  };
};

export default useOrgScopedAlternativeEmailModal;
